import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Container,
  CircularProgress,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Alert,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import { Outlet, useMatch, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { alpha } from "@mui/material/styles";
import {
  clearClientPortalSession,
  portalBootstrap,
  portalMe,
} from "../../api/clientPortal";
import ClientPortalUnavailablePage from "./ClientPortalUnavailablePage";
import {
  getClientPortalErrorMessage,
  isPortalUnavailableError,
} from "./clientPortalErrorMessage";

function resolvePortalFeatures(user) {
  if (user?.features && typeof user.features === "object") {
    return {
      solicitacoes: Boolean(user.features.solicitacoes),
      tarefas: Boolean(user.features.tarefas),
    };
  }
  return {
    solicitacoes:
      user?.portal_liberar_solicitacoes == null
        ? true
        : Number(user.portal_liberar_solicitacoes) === 1,
    tarefas: Number(user?.portal_liberar_tarefas) === 1,
  };
}

export default function ClientPortalLayout() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const company = params.get("company") || "";
  const q = company ? `?company=${encodeURIComponent(company)}` : "";

  const [brand, setBrand] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [unavailableMessage, setUnavailableMessage] = useState("");
  const location = useLocation();
  const ticketDetailMatch = useMatch("/portal-cliente/app/:id");
  const isTicketChat =
    Boolean(ticketDetailMatch) &&
    ticketDetailMatch.params?.id !== "new" &&
    ticketDetailMatch.params?.id !== "tasks";
  const isTasksPage = location.pathname.includes("/portal-cliente/app/tasks");
  const isNewTicketPage = location.pathname.includes("/portal-cliente/app/new");
  const features = useMemo(() => resolvePortalFeatures(user), [user]);
  const showTicketsTab = features.solicitacoes;
  const showTasksTab = features.tarefas;
  const showTabs = showTicketsTab && showTasksTab;
  const navTab = isTasksPage ? "tasks" : "tickets";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (company) {
          const b = await portalBootstrap(company);
          if (!cancelled) setBrand(b);
        }
        const me = await portalMe();
        if (!cancelled) setUser(me);
      } catch (err) {
        if (!cancelled) {
          if (isPortalUnavailableError(err)) {
            clearClientPortalSession();
            setUnavailable(true);
            setUnavailableMessage(
              getClientPortalErrorMessage(err, {
                context: "bootstrap",
                fallback:
                  "O portal ainda não está disponível para sua empresa. Peça ao suporte da empresa para habilitar o acesso.",
              })
            );
            return;
          }
          clearClientPortalSession();
          navigate(`/portal-cliente${q}`, { replace: true });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [company, navigate, q]);

  useEffect(() => {
    if (loading || !user) return;
    if (!showTicketsTab && !showTasksTab) return;

    if (!showTicketsTab && (isNewTicketPage || (!isTasksPage && !isTicketChat))) {
      navigate(`/portal-cliente/app/tasks${q}`, { replace: true });
      return;
    }
    if (!showTasksTab && isTasksPage) {
      navigate(`/portal-cliente/app${q}`, { replace: true });
    }
  }, [
    loading,
    user,
    showTicketsTab,
    showTasksTab,
    isTasksPage,
    isNewTicketPage,
    isTicketChat,
    navigate,
    q,
  ]);

  const logout = () => {
    clearClientPortalSession();
    navigate(`/portal-cliente${q}`);
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (unavailable) {
    return (
      <ClientPortalUnavailablePage
        message={unavailableMessage}
        companyName={brand?.name}
      />
    );
  }

  return (
    <Box
      sx={{
        height: { xs: "100dvh", md: "100vh" },
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: (t) =>
          t.palette.mode === "dark" ? "#0b0b0f" : alpha(t.palette.primary.main, 0.04),
      }}
    >
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          flexShrink: 0,
          bgcolor: "background.paper",
          color: "text.primary",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Toolbar sx={{ gap: 1 }}>
          {brand?.logo ? (
            <Box
              component="img"
              src={brand.logo}
              alt=""
              sx={{ height: 32, mr: 2, flexShrink: 0 }}
            />
          ) : null}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={800} noWrap>
              {brand?.name || "Portal do Cliente"}
            </Typography>
            {user?.nome ? (
              <Typography variant="caption" color="text.secondary" noWrap>
                {user.nome || user.razaosocial}
              </Typography>
            ) : null}
          </Box>
          <Tooltip title="Sair">
            <IconButton onClick={logout} aria-label="Sair da conta">
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
        {!isTicketChat && showTabs ? (
          <Tabs
            value={navTab}
            onChange={(_e, value) => {
              if (value === "tasks") navigate(`/portal-cliente/app/tasks${q}`);
              else navigate(`/portal-cliente/app${q}`);
            }}
            sx={{ px: 2, minHeight: 42, borderTop: "1px solid", borderColor: "divider" }}
          >
            <Tab value="tickets" label="Solicitações" sx={{ minHeight: 42, textTransform: "none" }} />
            <Tab value="tasks" label="Tarefas" sx={{ minHeight: 42, textTransform: "none" }} />
          </Tabs>
        ) : null}
      </AppBar>

      <Box
        component="main"
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: isTicketChat ? "hidden" : "auto",
          display: isTicketChat ? "flex" : "block",
          flexDirection: "column",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <Container
          maxWidth={isTicketChat || isTasksPage ? false : "md"}
          disableGutters={isTicketChat || isTasksPage}
          sx={{
            py: isTicketChat ? 0 : isTasksPage ? 2 : 3,
            px: isTasksPage ? { xs: 1.5, sm: 2.5, md: 3 } : undefined,
            flex: isTicketChat || isTasksPage ? 1 : undefined,
            display: isTicketChat || isTasksPage ? "flex" : "block",
            flexDirection: "column",
            minHeight: 0,
            width: "100%",
            maxWidth: isTasksPage ? "100% !important" : undefined,
          }}
        >
          {!showTicketsTab && !showTasksTab ? (
            <Alert severity="warning" sx={{ mx: 2 }}>
              Nenhum módulo foi liberado para o seu acesso. Fale com o atendimento da empresa.
            </Alert>
          ) : (
            <Outlet context={{ company, q, user, brand, features }} />
          )}
        </Container>
      </Box>
    </Box>
  );
}
